import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { LogOut, RefreshCw, Loader2, Eye } from "lucide-react";
import BrandMark from "../components/site/BrandMark";

const STATUS_VALUES = ["new", "in_progress", "scheduled", "resolved", "closed"];

const STATUS_BADGE = {
  new: "bg-blue-600 text-white",
  in_progress: "bg-amber-500 text-white",
  scheduled: "bg-indigo-600 text-white",
  resolved: "bg-emerald-600 text-white",
  closed: "bg-slate-500 text-white",
};

const URGENCY_BADGE = {
  low: "bg-slate-200 text-slate-800",
  medium: "bg-blue-100 text-blue-800",
  high: "bg-amber-100 text-amber-800",
  critical: "bg-red-600 text-white",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const labelize = (s) => (s || "").replace(/_/g, " ");

const StatBlock = ({ label, value, accent, testId }) => (
  <div data-testid={testId} className="border border-slate-200 bg-white p-5">
    <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-slate-500">{label}</div>
    <div className={`font-heading mt-2 text-3xl font-black ${accent || "text-slate-900"}`}>{value}</div>
  </div>
);

function ProtectedShell({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!user) return null;
  return children;
}

export default function AdminDashboard() {
  return (
    <ProtectedShell>
      <DashboardInner />
    </ProtectedShell>
  );
}

function DashboardInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [serviceReqs, setServiceReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sr] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/service-requests"),
      ]);
      setStats(s.data);
      setServiceReqs(sr.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/service-requests/${id}`, { status: newStatus });
      toast.success("Status updated");
      setServiceReqs((prev) => prev.map((x) => (x.id === id ? { ...x, status: newStatus } : x)));
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const filteredSR = serviceReqs.filter(
    (item) => statusFilter === "all" || item.status === statusFilter
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header data-testid="admin-header" className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <BrandMark className="h-12 w-12 drop-shadow-[0_2px_8px_rgba(37,99,235,0.4)]" />
            <div className="leading-none">
              <div className="font-heading font-black text-lg tracking-tight uppercase text-slate-900">
                Pneupro · Admin
              </div>
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">
                Operations console
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <span data-testid="admin-user-email" className="hidden sm:inline text-xs font-bold uppercase tracking-widest text-slate-600">
              {user?.email}
            </span>
            <button
              data-testid="admin-refresh-button"
              onClick={loadAll}
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-700 hover:text-blue-600"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 font-bold tracking-wide uppercase px-4 py-2 text-xs"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-blue-600">
              Dashboard
            </div>
            <h1 className="font-heading mt-2 text-4xl md:text-5xl font-black tracking-tighter uppercase text-slate-900">
              Service Operations
            </h1>
          </div>
        </div>

        {/* Stats */}
        <div data-testid="admin-stats-grid" className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
          <StatBlock testId="stat-sr-total" label="Total Requests" value={stats?.service_requests?.total ?? "—"} />
          <StatBlock testId="stat-sr-new" label="New" accent="text-blue-600" value={stats?.service_requests?.new ?? "—"} />
          <StatBlock testId="stat-sr-progress" label="In Progress" accent="text-amber-600" value={stats?.service_requests?.in_progress ?? "—"} />
          <StatBlock testId="stat-sr-scheduled" label="Scheduled" accent="text-indigo-600" value={stats?.service_requests?.scheduled ?? "—"} />
          <StatBlock testId="stat-sr-resolved" label="Resolved" accent="text-emerald-600" value={stats?.service_requests?.resolved ?? "—"} />
        </div>

        {/* Table */}
        <div className="mt-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tight uppercase text-slate-900">
              Service Requests
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                Filter:
              </span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="admin-status-filter" className="w-44 rounded-none">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_VALUES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`filter-option-${s}`}>
                      {labelize(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border border-slate-200 bg-white overflow-hidden">
            <Table data-testid="service-requests-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Urgency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <Loader2 className="h-5 w-5 animate-spin inline" />
                    </TableCell>
                  </TableRow>
                ) : filteredSR.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500 text-sm">
                      No service requests yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSR.map((r) => (
                    <TableRow key={r.id} data-testid={`sr-row-${r.id}`}>
                      <TableCell className="whitespace-nowrap text-xs text-slate-600">
                        {formatDate(r.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="font-bold text-slate-900">{r.name}</div>
                        <div className="text-xs text-slate-500">{r.email}</div>
                        <div className="text-xs text-slate-500">{r.phone}</div>
                      </TableCell>
                      <TableCell className="text-xs uppercase tracking-wide font-bold text-slate-700">
                        {r.service_type ? labelize(r.service_type) : "—"}
                      </TableCell>
                      <TableCell>
                        {r.urgency ? (
                          <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${URGENCY_BADGE[r.urgency]}`}>
                            {labelize(r.urgency)}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={r.status}
                          onValueChange={(v) => updateStatus(r.id, v)}
                        >
                          <SelectTrigger data-testid={`sr-status-select-${r.id}`} className="rounded-none w-36 h-9">
                            <SelectValue>
                              <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${STATUS_BADGE[r.status]}`}>
                                {labelize(r.status)}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_VALUES.map((s) => (
                              <SelectItem key={s} value={s}>
                                {labelize(s)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <button
                          data-testid={`sr-view-${r.id}`}
                          onClick={() => setDetail(r)}
                          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-blue-600 hover:underline"
                        >
                          <Eye className="h-3.5 w-3.5" /> View
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="detail-dialog" className="max-w-2xl rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Service Request Details
            </DialogTitle>
            <DialogDescription>
              {detail ? `Submitted ${formatDate(detail.created_at)}` : ""}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <Row label="Name" value={detail.name} />
              <Row label="Email" value={detail.email} />
              <Row label="Phone" value={detail.phone} />
              {detail.company && <Row label="Company" value={detail.company} />}
              {detail.service_type && <Row label="Service" value={labelize(detail.service_type)} />}
              {detail.urgency && <Row label="Urgency" value={labelize(detail.urgency)} />}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                  Description
                </div>
                <div className="border border-slate-200 bg-slate-50 p-4 whitespace-pre-wrap">
                  {detail.description}
                </div>
              </div>
              <Row label="Status" value={labelize(detail.status)} />
              <Row label="ID" value={detail.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 border-b border-slate-100 py-2">
    <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
    <div className="col-span-2 text-slate-800 break-words">{value}</div>
  </div>
);
